import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  LessonBlockMutationResponse,
  LessonBlockTranslationProjection,
  LessonBlockTranslationRecord,
  TranslationReplacementAction,
} from '@course-authoring/shared-types';
import type { BlockTranslationDecision } from '../../components/useBlockTranslationReplacementPrompt';

interface UseEnglishBlockTranslationDecisionsOptions {
  enabled: boolean;
  scopeKey: string;
  projection?: LessonBlockTranslationProjection;
  reviseDecision: (
    blockId: string,
    action: TranslationReplacementAction,
  ) => Promise<LessonBlockTranslationRecord>;
  refetchProjection: () => Promise<LessonBlockTranslationProjection | undefined>;
}

export function useEnglishBlockTranslationDecisions({
  enabled,
  scopeKey,
  projection,
  reviseDecision,
  refetchProjection,
}: UseEnglishBlockTranslationDecisionsOptions) {
  const [decisions, setDecisions] = useState<
    Record<string, BlockTranslationDecision | undefined>
  >({});
  const decisionsRef = useRef(decisions);
  const sourceRevisionByBlockRef = useRef(new Map<string, number>());
  const inFlightSourceRevisionRef = useRef(new Map<string, number>());
  const sourceChangedAwaitingDecisionRef = useRef(new Set<string>());
  decisionsRef.current = decisions;

  useEffect(() => {
    setDecisions({});
    sourceRevisionByBlockRef.current.clear();
    inFlightSourceRevisionRef.current.clear();
    sourceChangedAwaitingDecisionRef.current.clear();
  }, [scopeKey]);

  const onSourceChange = useCallback((blockId: string) => {
    const currentRevision = sourceRevisionByBlockRef.current.get(blockId) ?? 0;
    sourceRevisionByBlockRef.current.set(blockId, currentRevision + 1);
  }, []);

  const getReplacementActions = useCallback((): Record<string, TranslationReplacementAction> => {
    if (!enabled) return {};
    inFlightSourceRevisionRef.current = new Map(sourceRevisionByBlockRef.current);
    return Object.fromEntries(
      Array.from(inFlightSourceRevisionRef.current.keys()).flatMap((blockId) => {
        const decision = decisionsRef.current[blockId];
        return decision ? [[blockId, decision.action] as const] : [];
      }),
    );
  }, [enabled]);

  const onBlockContentSaved = useCallback((
    blockId: string,
    response: LessonBlockMutationResponse,
    replacementAction: TranslationReplacementAction | undefined,
  ) => {
    const savedRevision = inFlightSourceRevisionRef.current.get(blockId);
    if (savedRevision !== undefined && sourceRevisionByBlockRef.current.get(blockId) === savedRevision) {
      sourceRevisionByBlockRef.current.delete(blockId);
    }
    if (!response.translationSourceChanged) {
      if (savedRevision !== undefined) {
        setDecisions((current) => {
          if (current[blockId]?.committed) return current;
          const next = { ...current };
          delete next[blockId];
          return next;
        });
      }
      return;
    }
    if (replacementAction) {
      sourceChangedAwaitingDecisionRef.current.delete(blockId);
      setDecisions((current) => ({
        ...current,
        [blockId]: current[blockId] ? { ...current[blockId], committed: true } : undefined,
      }));
    } else {
      sourceChangedAwaitingDecisionRef.current.add(blockId);
    }
    void refetchProjection().then((refetchedProjection) => {
      if (!refetchedProjection || replacementAction !== 'queue') return;
      const record = refetchedProjection.blocks.find((block) => block.blockId === blockId);
      if ((record?.summary.pendingHumanTranslationCount ?? 0) === 0) return;
      setDecisions((current) => {
        const next = { ...current };
        delete next[blockId];
        return next;
      });
    });
  }, [refetchProjection]);

  const onDecision = useCallback((
    blockId: string,
    action: TranslationReplacementAction,
  ) => {
    const record = projection?.blocks.find((block) => block.blockId === blockId);
    const isRevision = decisionsRef.current[blockId]?.committed === true
      || sourceChangedAwaitingDecisionRef.current.has(blockId)
      || (record?.summary.pendingHumanTranslationCount ?? 0) > 0;
    setDecisions((current) => ({
      ...current,
      [blockId]: { action, committed: false },
    }));
    if (!isRevision || !enabled) return;
    void reviseDecision(blockId, action)
      .then((updatedRecord) => {
        sourceChangedAwaitingDecisionRef.current.delete(blockId);
        if (action === 'queue' && updatedRecord.summary.pendingHumanTranslationCount > 0) {
          setDecisions((current) => {
            const next = { ...current };
            delete next[blockId];
            return next;
          });
        } else {
          setDecisions((current) => ({
            ...current,
            [blockId]: { action, committed: true },
          }));
        }
        void refetchProjection();
      })
      .catch((decisionError) => {
        console.error('Failed to revise block translation decision:', decisionError);
      });
  }, [enabled, projection, refetchProjection, reviseDecision]);

  return {
    decisions,
    getReplacementActions,
    onBlockContentSaved,
    onDecision,
    onSourceChange,
  };
}
